/**
 * Auth Context - Proper Supabase Auth pattern for Vite/React SPA
 * 
 * Pattern:
 * 1. Set up onAuthStateChange listener FIRST (source of truth)
 * 2. Call getSession() to get initial state
 * 3. Use `finally` block to ALWAYS stop loading
 * 4. No timeouts - if something hangs, we need to fix the root cause
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Auth prompt type for components that trigger sign-in
export type AuthPrompt = {
  title?: string;
  description?: string;
  intent?: 'default' | 'tryon' | 'save' | 'chat';
};

interface AuthContextType {
  // Core auth state
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  
  // Auth modal state (preserved for backward compatibility with existing components)
  showSignInModal: boolean;
  setShowSignInModal: (show: boolean) => void;
  showWaitlistModal: boolean;
  setShowWaitlistModal: (show: boolean) => void;
  authPrompt: AuthPrompt | null;
  setAuthPrompt: (prompt: AuthPrompt | null) => void;
  closeAuthModal: () => void;
  
  // Auth helpers
  requireAuth: (onSuccess?: () => void, prompt?: AuthPrompt) => boolean;
  ensureGuestSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Modal state (kept for backward compatibility)
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [authPrompt, setAuthPrompt] = useState<AuthPrompt | null>(null);

  useEffect(() => {
    let mounted = true;

    // Async function to get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[Auth] getSession error:', error.message);
        }
        
        if (mounted && session) {
          setSession(session);
          setUser(session.user);
        }
      } catch (error) {
        console.error('[Auth] Session check failed:', error);
      } finally {
        // ✅ ALWAYS stop loading, even on error
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // 1. Set up the auth state listener FIRST (source of truth for OAuth callbacks)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session) => {
        if (mounted) {
          console.log('[Auth] State change:', event, session?.user?.email ?? 'no user');
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false); // ✅ Immediate UI update
        }
      }
    );

    // 2. Then get initial session (triggers OAuth code exchange if ?code= in URL)
    getInitialSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Sign out locally even if the API call fails
      console.error('[Auth] Sign out error:', error);
    }
  }, []);

  const closeAuthModal = useCallback(() => {
    setShowSignInModal(false);
    setShowWaitlistModal(false);
    setAuthPrompt(null);
  }, []);

  /**
   * requireAuth - Call this before gated actions
   * Returns true if user is authenticated, false otherwise
   * When false, redirects to /auth page (modal approach was removed)
   */
  const requireAuth = useCallback((onSuccess?: () => void, prompt?: AuthPrompt): boolean => {
    if (user) {
      onSuccess?.();
      return true;
    }
    
    // Best-effort: persist prompt for /auth page (if it ever wants to show context)
    if (typeof window !== 'undefined' && prompt) {
      try {
        sessionStorage.setItem('authPrompt', JSON.stringify(prompt));
      } catch {
        // ignore
      }
    }

    // Store the callback intent and redirect to auth page
    // The onSuccess callback will be handled by the redirect back
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/auth') {
        sessionStorage.setItem('authRedirect', currentPath);
      }
      
      // For onboarding try-on prompts, always redirect back to /onboard
      // This ensures users resume their onboarding flow after signup
      if (prompt?.intent === 'tryon' && currentPath === '/onboard') {
        sessionStorage.setItem('authRedirect', '/onboard');
      }
      
      window.location.href = '/auth';
    }
    
    return false;
  }, [user]);

  /**
   * ensureGuestSession - No-op placeholder
   * Guest sessions were removed; anonymous users use localStorage tracking
   */
  const ensureGuestSession = useCallback(async () => {
    // No-op: guest sessions removed, anonymous tracking handled elsewhere
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signOut,
        showSignInModal,
        setShowSignInModal,
        showWaitlistModal,
        setShowWaitlistModal,
        authPrompt,
        setAuthPrompt,
        closeAuthModal,
        requireAuth,
        ensureGuestSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
